import { useEffect, useState } from 'react';
import { useSession } from './session-context';
import { WorkspaceContext } from './workspace-context';
import { Organization } from '../interfaces/organization';
import { apiFetch } from '../lib/api';
import { ENDPOINTS } from '../constants/endpoints';
import { Sentry } from '../lib/sentry';

const WORKSPACE_STORAGE_KEY = 'client_workspace_id';

type Props = { children: React.ReactNode };

export const WorkspaceProvider = ({ children }: Props) => {
  const { session } = useSession();
  const [currentWorkspace, setCurrentWorkspace] = useState<Organization | null>(
    null,
  );
  const [availableWorkspaces, setAvailableWorkspaces] = useState<
    Organization[]
  >([]);
  const [isLoadingWorkspaces, setIsLoadingWorkspaces] = useState(true);
  const [canSwitchWorkspaces, setCanSwitchWorkspaces] = useState(false);

  useEffect(() => {
    const fetchWorkspaces = async () => {
      if (!session?.user) {
        setIsLoadingWorkspaces(false);
        return;
      }

      try {
        // Fetch workspaces from backend API
        const response = await apiFetch(ENDPOINTS.WORKSPACE_LIST);

        if (!response.ok) {
          throw new Error('Failed to fetch workspaces');
        }

        const data = await response.json();
        const workspaces: Organization[] = data.workspaces.map(
          (w: { id: number; name: string; isDefault: boolean }) => ({
            id: w.id,
            name: w.name,
          }),
        );

        // Find the default workspace
        const defaultWorkspace = data.workspaces.find(
          (w: { isDefault: boolean }) => w.isDefault,
        );

        // Check if user can switch workspaces (has more than one)
        const canSwitch = workspaces.length > 1;
        setCanSwitchWorkspaces(canSwitch);
        setAvailableWorkspaces(workspaces);

        // Try to get saved workspace from localStorage
        const savedWorkspaceId = localStorage.getItem(WORKSPACE_STORAGE_KEY);

        if (savedWorkspaceId && canSwitch) {
          const savedWorkspace = workspaces.find(
            (w: Organization) => w.id === parseInt(savedWorkspaceId),
          );
          if (savedWorkspace) {
            setCurrentWorkspace(savedWorkspace);
          } else {
            // Saved workspace not available, use default
            const defaultOrg = defaultWorkspace
              ? { id: defaultWorkspace.id, name: defaultWorkspace.name }
              : workspaces[0];
            setCurrentWorkspace(defaultOrg);
            localStorage.setItem(
              WORKSPACE_STORAGE_KEY,
              defaultOrg.id.toString(),
            );
          }
        } else {
          // No saved workspace or single org user, use default
          const defaultOrg = defaultWorkspace
            ? { id: defaultWorkspace.id, name: defaultWorkspace.name }
            : workspaces[0];
          if (defaultOrg) {
            setCurrentWorkspace(defaultOrg);
            localStorage.setItem(
              WORKSPACE_STORAGE_KEY,
              defaultOrg.id.toString(),
            );
          }
        }
      } catch (error) {
        Sentry.captureException(error);
      } finally {
        setIsLoadingWorkspaces(false);
      }
    };

    fetchWorkspaces();
  }, [session]);

  const switchWorkspace = (orgId: number) => {
    const workspace = availableWorkspaces.find((w) => w.id === orgId);
    if (workspace) {
      setCurrentWorkspace(workspace);
      localStorage.setItem(WORKSPACE_STORAGE_KEY, orgId.toString());
    }
  };

  return (
    <WorkspaceContext.Provider
      value={{
        currentWorkspace,
        availableWorkspaces,
        switchWorkspace,
        isLoadingWorkspaces,
        canSwitchWorkspaces,
      }}
    >
      {children}
    </WorkspaceContext.Provider>
  );
};
